import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { StatCard } from '../../components/ui/StatCard'
import { LineChart } from '../../components/charts/LineChart'
import { EmptyState } from '../../components/ui/EmptyState'
import styles from './AdminDashboard.module.css'

interface DatiAdminDashboard {
  totale_utenti: number
  utenti_attivi_oggi: number
  utenti_attivi_settimana: number
  nuovi_utenti_mese: number
  crescita_utenti_pct: number
  totale_team: number
  team_attivi: number
  sessioni_oggi: number
  sessioni_settimana: number
  sessioni_mese: number
  sessioni_totali: number
  minuti_audio_totali: number
  durata_media_sessione_secondi: number
  mrr: number
  arr: number
  revenue_mese: number
  crescita_revenue_pct: number
  paganti_totali: number
  churn_rate_pct: number
  costo_api_mese: number
  costo_medio_per_sessione: number
  margine_pct: number
  obiettivi_con_obiettivo: number
  obiettivi_raggiunti: number
  obiettivi_percentuale: number
  grafico_utenti_30_giorni: Array<{ data: string; nuovi: number; totale: number }>
  grafico_sessioni_30_giorni: Array<{ data: string; sessioni: number }>
}

export function AdminDashboard() {
  const q = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () =>
      (await api.get<{ dati: DatiAdminDashboard }>('/superadmin/dashboard')).data.dati,
  })

  if (q.isLoading) return <div className={styles.skeleton}>Caricamento…</div>
  if (q.error || !q.data) {
    return <EmptyState titolo="Errore caricamento" descrizione="Backend non raggiungibile" />
  }

  const d = q.data
  return (
    <div className={styles.wrap}>
      <section>
        <h3 className={styles.titoloSezione}>Utenti</h3>
        <div className={styles.grid4}>
          <StatCard etichetta="Totale" valore={d.totale_utenti} />
          <StatCard etichetta="Attivi settimana" valore={d.utenti_attivi_settimana} />
          <StatCard etichetta="Nuovi mese" valore={d.nuovi_utenti_mese} delta={d.crescita_utenti_pct} deltaSuffisso="vs mese scorso" />
          <StatCard etichetta="Paganti" valore={d.paganti_totali} />
        </div>
      </section>

      <section>
        <h3 className={styles.titoloSezione}>Sessioni</h3>
        <div className={styles.grid4}>
          <StatCard etichetta="Oggi" valore={d.sessioni_oggi} />
          <StatCard etichetta="Settimana" valore={d.sessioni_settimana} />
          <StatCard etichetta="Mese" valore={d.sessioni_mese} />
          <StatCard etichetta="Durata media" valore={d.durata_media_sessione_secondi} unita="s" />
        </div>
      </section>

      <section>
        <h3 className={styles.titoloSezione}>Revenue & costi</h3>
        <div className={styles.grid4}>
          <StatCard etichetta="MRR" valore={d.mrr.toFixed(0)} unita="€" delta={d.crescita_revenue_pct} deltaSuffisso="vs mese scorso" />
          <StatCard etichetta="ARR" valore={d.arr.toFixed(0)} unita="€" />
          <StatCard etichetta="Costi AI mese" valore={d.costo_api_mese.toFixed(0)} unita="€" />
          <StatCard etichetta="Margine" valore={d.margine_pct.toFixed(1)} unita="%" delta={d.margine_pct > 50 ? 1 : -1} deltaSuffisso="" />
        </div>
      </section>

      <section>
        <h3 className={styles.titoloSezione}>🎯 Obiettivi (mese corrente)</h3>
        <div className={styles.grid4}>
          <StatCard
            etichetta="Sessioni con obiettivo"
            valore={d.obiettivi_con_obiettivo}
          />
          <StatCard
            etichetta="Obiettivi raggiunti"
            valore={d.obiettivi_raggiunti}
          />
          <StatCard
            etichetta="Tasso di successo"
            valore={d.obiettivi_percentuale.toFixed(1)}
            unita="%"
          />
        </div>
      </section>

      <div className={styles.grafici}>
        <Card titolo="Nuovi utenti / giorno (30 gg)">
          <LineChart
            dati={d.grafico_utenti_30_giorni}
            chiaveX="data"
            chiaveY="nuovi"
          />
        </Card>
        <Card titolo="Sessioni / giorno (30 gg)">
          <LineChart
            dati={d.grafico_sessioni_30_giorni}
            chiaveX="data"
            chiaveY="sessioni"
          />
        </Card>
      </div>
    </div>
  )
}
